import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IContributionMarginLevel } from '../../../models/contribution-margin-level.model';
import { distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { IContributionMarginLevelState } from '../../../services/facades/master-data/contribution-margin-level-state.model';
import { ContributionMarginLevelFacade } from '../../../services/facades/master-data/contribution-margin-level-facade.service';
import { ContributionMarginLevelDialogComponent } from './contribution-margin-level-dialog/contribution-margin-level-dialog.component';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';

@Component({
    templateUrl: './contribution-margin-level.page.html',
    styleUrls: [ './contribution-margin-level.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContributionMarginLevelPage implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild(MatSort)
    public sort: MatSort;

    public dataSource: MatTableDataSource<IContributionMarginLevel> = new MatTableDataSource<IContributionMarginLevel>([]);
    public costGroupState$: Observable<IContributionMarginLevelState>;
    private destroy$: Subject<void> = new Subject();

    constructor(private myContributionMarginLevelFacade: ContributionMarginLevelFacade,
                private myMatDialog: MatDialog) {
    }

    public ngOnInit(): void {
        this.costGroupState$ = this.myContributionMarginLevelFacade.subState();
        this.costGroupState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IContributionMarginLevelState) => s.data),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: IContributionMarginLevel[]) => {
                this.dataSource.data = data;
            });
        this.dataSource.filterPredicate = (contributionLevel, filterValue): boolean => {
            filterValue = filterValue.trim().toLowerCase();

            if (contributionLevel.name.trim().toLowerCase().includes(filterValue)){
                return true;
            } else {
                return false;
            }
        };
        void this.myContributionMarginLevelFacade.fetchData();
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myContributionMarginLevelFacade.clearState();
    }

    public onEdit(contributionMarginLevel: IContributionMarginLevel): void {
        const dialog: MatDialogRef<ContributionMarginLevelDialogComponent> = this.myMatDialog.open(ContributionMarginLevelDialogComponent, {
            width: '600px',
            data: contributionMarginLevel,
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void this.myContributionMarginLevelFacade.fetchData();
            }
        });
    }

    public onCreate(): void {
        const dialog: MatDialogRef<ContributionMarginLevelDialogComponent> = this.myMatDialog.open(ContributionMarginLevelDialogComponent, {
            width: '600px',
            data: {},
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void this.myContributionMarginLevelFacade.fetchData();
            }
        });
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim().toLowerCase(); // Remove whitespace, MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    public onDeleteContributionMarginLevel(contributionMarginLevel: IContributionMarginLevel): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.contribution-margin-level.confirm-delete.label',
                messageParams: { name: contributionMarginLevel.name }
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myContributionMarginLevelFacade.deleteContributionMarginLevel(contributionMarginLevel.id);
            }
        });
    }

}
